import { txt } from '../utils/dom.js';
import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';

// ── TIMELINE ──────────────────────────────────────────
export const timelineBlock = (rawProps) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    const title = p.title || 'Nuestra Historia';
    const subtitle = p.subtitle || 'El camino que nos trajo hasta aquí';
    const events = p.events || [
        { year: '2019', title: 'Los Inicios', desc: 'Fundamos la empresa con una visión clara.' },
        { year: '2020', title: 'Primer Cliente', desc: 'Lanzamos nuestro producto al mercado.' },
        { year: '2022', title: 'Expansión', desc: 'Crecimos hasta 50 empleados en 3 países.' },
        { year: '2024', title: 'Reconocimiento', desc: 'Premiados como mejor startup del año.' },
    ];

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? "#ffffff" : "#111827";
    const btnIcon = isDark ? "#111827" : "#ffffff";

    // DUOTONO DE MARCA: el PUNTO de la línea de tiempo y el año (kicker) son decoración, no un
    // CTA → estrenan el segundo color del cliente. `var(--secondary, var(--accent))` cae al
    // acento cuando el sitio no tiene secundario, así que el render es idéntico al de antes.
    // La var la declara render.js/compileSiteHtml en el <section> (ver utils/contrast.js).
    const DUO = 'var(--secondary, var(--accent))';
    const dotStyle = `background:${DUO}; box-shadow:0 0 0 4px color-mix(in srgb, ${DUO} 20%, transparent);`;

    // --- Toolbar compartida ---
    const itemToolbar = (i, direction = 'vertical') => {
        const addTop = direction === 'vertical'
            ? (i !== 0 ? `<button class="item-add-btn card-add-top" data-array="events" data-index="${i}">+</button>` : '')
            : (i !== 0 ? `<button class="item-add-btn card-add-left" data-array="events" data-index="${i}">+</button>` : '');
        const addBottom = direction === 'vertical'
            ? `<button class="item-add-btn card-add-bottom" data-array="events" data-index="${i}">+</button>`
            : `<button class="item-add-btn card-add-right" data-array="events" data-index="${i}">+</button>`;
            
        return `
        ${addTop}
        ${addBottom}
        <div class="item-tools">
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn item-delete-btn" data-array="events" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;
    };

    // --- Encabezado compartido ---
    const header = `
        <div style="text-align:center; margin-bottom:80px;">
            <h2 data-field="title" contenteditable="true" style="font-size:calc(clamp(2rem,4cqw,3rem) * var(--type-scale, 1)); font-weight:900; margin-bottom:16px;">${title}</h2>
            <p data-field="subtitle" contenteditable="true" style="opacity:0.6; font-size:calc(1.1rem * var(--text-scale, 1));">${subtitle}</p>
        </div>`;

    // --- Card de evento compartida ---
    const eventCard = (ev) => `
        <span class="ghost-number" aria-hidden="true" style="font-size: clamp(4rem, 8cqw, 6rem); top: 50%; right: 10px; transform: translateY(-50%); z-index: 0;">${txt(ev.year)}</span>
        <span class="tl-year" style="color:${DUO}; font-weight:900; font-size:calc(0.85rem * var(--type-scale, 1)); letter-spacing:var(--kicker-tracking, 2px); text-transform:var(--kicker-case, uppercase);"
              data-field="year" contenteditable="true">${txt(ev.year)}</span>
        <h3 class="tl-title" style="font-size:calc(1.3rem * var(--heading-scale, var(--u-type-scale, 1))); font-weight:800; margin:8px 0 10px;"
            data-field="tl-title" contenteditable="true">${txt(ev.title)}</h3>
        <p class="tl-desc" style="opacity:0.7; line-height:1.6;"
           data-field="tl-desc" contenteditable="true">${txt(ev.desc)}</p>`;

    // --- v1 / v4: zigzag alternante con línea vertical central ---
    // La v4 ("Historia con scroll") comparte MARKUP con la v1 a propósito: lo único que
    // cambia es la coreografía (`variant-animations.js` → `timelineSticky`), que en
    // preview/publicado sustituye esta sección por un panel `position:sticky`. En el editor,
    // y con `prefers-reduced-motion`, la v4 se ve exactamente como la v1 — que es justo el
    // respaldo que se quiere cuando el scroll-jack no puede correr.
    if (variant === 'v1' || variant === 'v4' || (!variant || variant === '')) {
        return `
    <section id="bloque-timeline" class="reveal variant-${variant} timeline-layout" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
        ${header}
        <div class="timeline-container" style="--tl-accent:${p.accentColor}; width: 100%; max-width: 1100px; margin: 0 auto; display: flex; flex-direction: column; gap: 40px;">
            <div class="tl-line" style="background:linear-gradient(180deg,transparent,var(--accent),transparent);"></div>
            ${events.map((ev, i) => {
                const isLeft = i % 2 === 0;
                return `
            <div class="tl-item ${isLeft ? 'tl-left' : 'tl-right'} stagger-item sortable-item" data-item-index="${i}" data-array-name="events" style="--stagger-i:${i}; position:relative; width: 100%; min-height: 120px; display: flex; align-items: center; justify-content: ${isLeft ? 'flex-end' : 'flex-start'};">
                <div class="tl-dot" style="${dotStyle}"></div>
                <div class="tl-card" style="background:var(--card-bg, color-mix(in srgb, var(--accent) 5%, transparent)); border:var(--card-border, 1px solid color-mix(in srgb, var(--accent) 13%, transparent)); border-radius:20px; padding:28px 32px; width: 85%; max-width: 480px; overflow-wrap: anywhere; word-break: break-word;">
                    ${eventCard(ev)}
                </div>
                ${itemToolbar(i)}
            </div>`;
            }).join('')}
        </div>
    </section>`;
    }

    // --- v2 ("linear"): línea a la izquierda, todo el contenido a la derecha ---
    if (variant === 'v2') {
        return `
    <section id="bloque-timeline" class="reveal variant-${variant} timeline-layout" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
        ${header}
        <div style="max-width: 700px; margin: 0 auto; position: relative; padding-left: 50px;">
            <!-- Línea vertical izquierda -->
            <div style="position: absolute; left: 18px; top: 0; bottom: 0; width: 2px; background: linear-gradient(180deg, transparent, var(--accent), transparent);"></div>
            ${events.map((ev, i) => `
            <div class="stagger-item sortable-item" data-item-index="${i}" data-array-name="events" style="--stagger-i:${i}; position: relative; padding: 0 0 48px 0;">
                <!-- Dot -->
                <div style="position: absolute; left: -31px; top: 4px; width: 14px; height: 14px; border-radius: 50%; ${dotStyle} transform: translateX(-50%);"></div>
                ${eventCard(ev)}
                ${itemToolbar(i)}
            </div>`).join('')}
        </div>
    </section>`;
    }

    // --- v3 ("horizontal"): timeline horizontal con scroll-snap ---
    return `
    <section id="bloque-timeline" class="reveal variant-${variant} timeline-layout" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
        ${header}
        <div style="position: relative;">
            <!-- Línea horizontal alineada con precisión a los nodos -->
            <div style="position: absolute; top: 59px; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--accent), transparent);"></div>
            <div class="tl-v3-track">
                ${events.map((ev, i) => `
                <div class="stagger-item sortable-item tl-v3-item" data-item-index="${i}" data-array-name="events" 
                     style="--stagger-i:${i};">
                    <!-- Dot centrado -->
                    <div style="position: absolute; left: 50%; top: -7px; transform: translateX(-50%); width: 14px; height: 14px; border-radius: 50%; ${dotStyle} z-index: 2; transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.3s;"></div>
                    <div class="tl-v3-card" style="background: color-mix(in srgb, var(--bg) 96%, var(--text)); border: 1px solid color-mix(in srgb, var(--text) 8%, transparent); border-radius: var(--card-radius, 20px); padding: 32px 28px; overflow-wrap: anywhere; word-break: break-word; margin-top: 16px; transition: transform 0.3s, box-shadow 0.3s; box-shadow: 0 4px 20px transparent;">
                        ${eventCard(ev)}
                    </div>
                    ${itemToolbar(i, 'horizontal')}
                </div>`).join('')}
            </div>
        </div>
    </section>`;
};
